<template>
  <div class="scheduled-effect" :class="{ '-played': roundPlayed }">
    <label class="scheduled-effect__label" @click="inputField?.focus()">
      {{ affectedItem?.title ?? $t("labels.unnamed") }}
    </label>

    <expression-input
      :id="effect.id"
      ref="inputField"
      :expression="effect.impulseExpressions?.[0]"
      :disabled="roundPlayed"
      :parse-context="{
        targets,
        resources,
        indices,
        rounds: closestEvaluatedRound ? [closestEvaluatedRound] : [],
        affectedType: effect.affectedType,
        affectedId: effect.affectedId
      }"
      :style="{ minWidth: '5rem' }"
      default-sign="+"
      @update:expression="onPointUpdate($event)"
    />

    <button
      v-if="!roundPlayed"
      type="button"
      :title="$t('actions.delete')"
      class="button-icon button_slim"
      @click="$emit('delete')"
    >
      <lucide-icon name="trash-2" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import LucideIcon from "@core/ui/LucideIcon.vue";
import ExpressionInput from "@editor/items/expression/ExpressionInput.vue";

import Simulation from "@editor/items/simulation/Simulation";
import { useModelStore } from "@editor/items/model/model.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";

import type { ScheduledEffectItem } from "./ScheduledEffectItem";

const props = defineProps<{
  effect: ScheduledEffectItem;
  roundIndex: number;
}>();

const emit = defineEmits<{
  (e: "delete"): void;
  (e: "update:effect", value: ScheduledEffectItem): void;
}>();

const inputField = ref<InstanceType<typeof ExpressionInput>>();

const simulationViewStore = useSimulationViewStore();

const simulation = computed(() => {
  return simulationViewStore.simulation;
});

const indices = computed(() => useIndexStore().items);
const targets = computed(() => simulationViewStore.targets);
const resources = computed(() => simulationViewStore.resources);

const roundPlayed = computed(() => {
  if (!simulation.value) {
    return false;
  }
  if (simulation.value.complete) {
    simulation.value.currentRoundIndex >= props.roundIndex;
  }
  return simulation.value.currentRoundIndex > props.roundIndex;
});

const closestEvaluatedRound = computed(() => {
  if (!simulation.value) {
    return;
  }
  let index = props.roundIndex;
  if (index > simulationViewStore.currentRoundIndex) {
    index = simulationViewStore.currentRoundIndex;
  }
  if (!simulation.value.complete) {
    index -= 1;
  }
  if (index < 0) {
    const sim = new Simulation(simulation.value);
    return sim.virtualGameStartRound;
  }
  return simulation.value.rounds[index];
});

const affectedItem = computed(() => {
  return useModelStore().getItem(
    props.effect.affectedType,
    props.effect.affectedId
  );
});


function onPointUpdate(newAmount: string) {
  emit("update:effect", {
    ...props.effect,
    impulseExpressions: [newAmount]
  });
}
function focus() {
  inputField.value?.focus();
}

defineExpose({
  focus
});
</script>

<style lang="scss">
.scheduled-effect {
  display: grid;
  grid-template-columns: minmax(6em, 1fr) fit-content(20rem) 2rem;
  gap: 0.25rem;
  align-items: center;
  background: var(--color-background);
  padding: 0.25rem;
  padding-left: 0.5rem;
  border-radius: 5px;

  &.-played {
    grid-template-columns: minmax(6rem, 1fr) fit-content(20rem);
    padding-right: 0.5rem;
  }

  &__label {
    overflow-wrap: break-word;
  }
}
</style>
